import { useTranslation } from 'react-i18next';
import type { IDocumentAnnotation } from '@apis/documentAnnotations';
import { formatProjectDateTime } from '@utils/formatProjectDate';
import { TrashIcon } from '@utils/icons';
import styles from './AnnotationRow.module.css';

interface IAnnotationRowProps {
  annotation: IDocumentAnnotation;
  visible: boolean;
  tz: string | null;
  canDelete: boolean;
  onToggle: () => void;
  onDelete: () => void;
}

/** Initials for the author's avatar — two letters at most, like the comms roster. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0]![0]! + (parts[1]?.[0] ?? '')).toUpperCase();
}

export function AnnotationRow({ annotation, visible, tz, canDelete, onToggle, onDelete }: IAnnotationRowProps) {
  const { t } = useTranslation();

  return (
    <li className={`${styles.row} ${visible ? '' : styles.rowOff}`}>
      <input
        type="checkbox"
        className={styles.checkbox}
        checked={visible}
        onChange={onToggle}
        aria-label={t('documents.annotations.toggleVisibility', { author: annotation.createdBy.name })}
      />
      <span className={styles.avatar} aria-hidden="true">{initialsOf(annotation.createdBy.name)}</span>
      <div className={styles.rowMain}>
        <span className={styles.author}>{annotation.createdBy.name}</span>
        {annotation.note && <p className={styles.note}>{annotation.note}</p>}
        <span className={styles.marks}>{t('documents.annotations.markCount', { count: annotation.items.length })}</span>
      </div>

      {/* When it was drawn, and — beneath it — the one action on it. */}
      <div className={styles.rowSide}>
        <span className={styles.when}>{tz ? formatProjectDateTime(annotation.createdAt, tz) : '—'}</span>
        {canDelete && (
          <button
            type="button"
            className={styles.delete}
            onClick={onDelete}
            aria-label={t('documents.annotations.delete')}
          >
            <TrashIcon size={14} />
          </button>
        )}
      </div>
    </li>
  );
}
